import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CornerDownLeft, Sparkles } from "lucide-react";
import { api } from "../../api";

export const slugify = (s = "") => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 48);

// Open the ⌘K palette in Ask mode from anywhere: openAsk({ q, context: { type, id, title }, auto }).
export const openAsk = (detail = {}) => window.dispatchEvent(new CustomEvent("traide:ask", { detail }));

export function useAsk() {
  const [state, set] = useState({ q: "", busy: false, res: null, err: false });
  const seq = useRef(0);
  const ask = async (q, context) => {
    const id = ++seq.current;
    set({ q, busy: true, res: null, err: false });
    try {
      const r = await api.post("/intelligence/ask", { q, context: context || null });
      if (id === seq.current) set({ q, busy: false, res: r.data, err: false });
    } catch {
      if (id === seq.current) set({ q, busy: false, res: null, err: true });
    }
  };
  const reset = () => { seq.current++; set({ q: "", busy: false, res: null, err: false }); };
  return { ...state, ask, reset };
}

export function useSuggestions() {
  const [chips, setChips] = useState([]);
  useEffect(() => { api.get("/intelligence/suggestions").then((r) => setChips(r.data.chips || [])).catch(() => {}); }, []);
  return chips;
}

export const contextChips = (ctx) => ctx ? ["Why is the margin where it is?", `What needs attention on this ${ctx.type}?`, "What should I do next here?"] : [];

export function Thinking({ label = "TRAIDE is reading your records", testid = "ask-thinking" }) {
  return <div className="t3 flex items-center gap-2 text-[12.5px]" data-testid={testid}><span className="intel-thinking"><i /><i /><i /></span>{label}…</div>;
}

export function AskChips({ chips, onPick, testid = "ask-chips" }) {
  if (!chips.length) return null;
  return <div className="flex flex-wrap gap-1.5" data-testid={testid}>{chips.map((c) => <button key={c} type="button" className="intel-chip" onClick={() => onPick(c)} data-testid={`ask-chip-${slugify(c)}`}>{c}</button>)}</div>;
}

export function AskAnswer({ res, onGo, testid = "ask-answer" }) {
  const go = (to) => (e) => { if (onGo) { e.preventDefault(); onGo(to); } };
  return (
    <div data-testid={testid} className="intel-answer">
      <p className="t1 text-[14px] leading-relaxed" data-testid="ask-answer-text">{res.answer}</p>
      {res.lines?.length > 0 && <div className="mt-2" data-testid="ask-lines">{res.lines.map((l, i) => <div key={i} className="intel-line"><i />{l.link ? <Link to={l.link} onClick={go(l.link)} className="min-w-0 hover:underline" data-testid={`ask-line-${i}`}>{l.text}</Link> : <span className="min-w-0">{l.text}</span>}</div>)}</div>}
      {res.actions?.length > 0 && <div className="mt-3 flex flex-wrap gap-2" data-testid="ask-actions">{res.actions.map((a, i) => <Link key={i} to={a.link} onClick={go(a.link)} className="btn-secondary btn-sm" data-testid={`ask-action-${i}`}>{a.label} <ArrowRight size={12} /></Link>)}</div>}
      <div className="t4 mt-3 text-[11px]">Answered from your live records{res.context ? " for this record" : ""}. TRAIDE prepares — nothing changes until you confirm it.</div>
    </div>
  );
}

// Inline ask box for a page (Intelligence Center). Chips until the first question; answer stays until cleared.
export function AskBar({ context, placeholder = "Ask TRAIDE anything about the business — jobs, money, people, materials…", testid = "ask-bar" }) {
  const { q, busy, res, err, ask, reset } = useAsk();
  const chips = useSuggestions();
  const [text, setText] = useState("");
  const run = (s) => { const v = (s || "").trim(); if (v) { setText(v); ask(v, context); } };
  return (
    <section className="card p-4" data-testid={testid}>
      <form onSubmit={(e) => { e.preventDefault(); run(text); }} className="flex items-center gap-3">
        <span className="intel-mark"><Sparkles size={14} /></span>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} className="t1 h-10 min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-faint" data-testid="ask-input" />
        <button type="submit" className="btn-primary btn-sm" disabled={busy || !text.trim()} data-testid="ask-submit">Ask <CornerDownLeft size={12} /></button>
      </form>
      {!res && !busy && !err && <div className="mt-3 pl-10"><AskChips chips={context ? contextChips(context) : chips} onPick={run} /></div>}
      {busy && <div className="mt-3 pl-10"><Thinking /></div>}
      {err && <div className="mt-3 pl-10 text-[13px] text-rose-600" data-testid="ask-error">TRAIDE couldn't answer that right now. <button type="button" className="underline" onClick={() => run(q)}>Try again</button></div>}
      {res && (
        <div className="mt-4 pl-10">
          <div className="t3 mb-2 text-[12px]">You asked · <span className="t2">{q}</span><button type="button" className="ml-3 underline" onClick={() => { reset(); setText(""); }} data-testid="ask-clear">Clear</button></div>
          <AskAnswer res={res} />
        </div>
      )}
    </section>
  );
}
